export type ToolStatus = "streaming" | "pending" | "done" | "error";

export interface ToolInfo {
  id: string;
  name?: string;
  status: ToolStatus;
  error?: string;
}

/**
 * ToolTracker keeps the authoritative status for tool calls.
 * - Status state is stored separately from execution promises
 * - Provides derived views (active ids, has pending)
 * - Small, synchronous API suitable for use inside the engine
 */
export class ToolTracker {
  private readonly tools = new Map<string, ToolInfo>();
  // Non-serialized execution promises
  private readonly execPromises = new Map<
    string,
    Promise<{ toolCallId: string; result: string }>
  >();

  startRequest(id: string, name?: string): void {
    this.tools.set(id, { id, name, status: "streaming" });
  }

  /**
   * Mark that the request finished and execution is now pending.
   * Optionally pass the execution promise to be tracked separately.
   */
  markPendingExec(
    id: string,
    name?: string,
    promise?: Promise<{ toolCallId: string; result: string }>
  ): void {
    const prev = this.tools.get(id);
    this.tools.set(id, { id, name: name ?? prev?.name, status: "pending" });
    if (promise) this.execPromises.set(id, promise);
  }

  markDone(id: string): void {
    const prev = this.tools.get(id);
    if (!prev) return;
    this.tools.set(id, { ...prev, status: "done", error: undefined });
    this.execPromises.delete(id);
  }

  markError(id: string, error: string): void {
    const prev = this.tools.get(id);
    if (!prev) return;
    this.tools.set(id, { ...prev, status: "error", error });
    this.execPromises.delete(id);
  }

  get(id: string): ToolInfo | undefined {
    return this.tools.get(id);
  }

  /** Active tools are those still streaming inputs or pending execution */
  activeIds(): string[] {
    const ids: string[] = [];
    for (const [id, info] of this.tools) {
      if (info.status === "streaming" || info.status === "pending")
        ids.push(id);
    }
    return ids;
  }

  /** Any tool still streaming inputs or with execution pending */
  hasPending(): boolean {
    for (const info of this.tools.values()) {
      if (info.status === "streaming" || info.status === "pending") return true;
    }
    return false;
  }

  /** Any tool currently streaming request inputs */
  hasStreamingRequests(): boolean {
    for (const info of this.tools.values()) {
      if (info.status === "streaming") return true;
    }
    return false;
  }

  /** Return shallow copies for safe read-only views */
  values(): ToolInfo[] {
    return Array.from(this.tools.values()).map((t) => ({ ...t }));
  }

  /** Get the stored execution promise, if any */
  getPromise(
    id: string
  ): Promise<{ toolCallId: string; result: string }> | undefined {
    return this.execPromises.get(id);
  }

  /** Store/replace the execution promise for a tool id */
  setPromise(
    id: string,
    promise: Promise<{ toolCallId: string; result: string }>
  ): void {
    this.execPromises.set(id, promise);
  }

  /** Clear all tracking */
  clear(): void {
    this.tools.clear();
    this.execPromises.clear();
  }
}
